import React, { useState } from "react";
import { WhatsAppConversation } from "../../types/api";
import { normalizePhone } from "../../utils/phoneUtils";

interface PhoneGroup {
  phone_number: string;
  patient_name?: string;
  patient_hm_id?: string;
  patient_id?: number;
  unread_count: number;
  last_message_at?: string;
  last_message_preview?: string;
  can_send_freeform: boolean;
  assigned_user_name?: string;
  status?: 'open' | 'solved' | 'pending';
}

function groupByPhone(conversations: WhatsAppConversation[]): PhoneGroup[] {
  const map = new Map<string, PhoneGroup>();
  for (const conv of conversations) {
    const existing = map.get(conv.phone_number);
    if (!existing) {
      map.set(conv.phone_number, {
        phone_number: conv.phone_number,
        patient_name: conv.patient_name,
        patient_hm_id: conv.patient_hm_id,
        patient_id: conv.patient_id,
        unread_count: conv.unread_count,
        last_message_at: conv.last_message_at,
        last_message_preview: conv.last_message_preview,
        can_send_freeform: conv.can_send_freeform,
        assigned_user_name: conv.assigned_user_name,
        status: conv.status,
      });
    } else {
      existing.unread_count += conv.unread_count;
      if (conv.last_message_at && (!existing.last_message_at || conv.last_message_at > existing.last_message_at)) {
        existing.last_message_at = conv.last_message_at;
        existing.last_message_preview = conv.last_message_preview;
      }
      if (conv.can_send_freeform) existing.can_send_freeform = true;
      if (!existing.patient_name && conv.patient_name) {
        existing.patient_name = conv.patient_name;
        existing.patient_hm_id = conv.patient_hm_id;
        existing.patient_id = conv.patient_id;
      }
      if (!existing.assigned_user_name && conv.assigned_user_name) {
        existing.assigned_user_name = conv.assigned_user_name;
      }
      if (conv.status && !existing.status) existing.status = conv.status;
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    if (!a.last_message_at) return 1;
    if (!b.last_message_at) return -1;
    return b.last_message_at.localeCompare(a.last_message_at);
  });
}

const STATUS_COLORS: Record<string, string> = {
  open: "#25D366",
  solved: "#999",
  pending: "#f59e0b",
  campaign: "#8b5cf6",
};

const STATUS_LABELS: Record<string, string> = {
  open: "פתוח",
  solved: "סגור",
  pending: "ממתין",
  campaign: "קמפיין",
};

type StatusFilter = 'all' | 'open' | 'solved' | 'pending' | 'campaign';

interface ConversationListProps {
  conversations: WhatsAppConversation[];
  selectedPhone?: string;
  isLoading: boolean;
  onSelect: (group: PhoneGroup) => void;
  onSearch: (query: string) => void;
  onStatusFilter?: (status: StatusFilter) => void;
  activeStatusFilter?: StatusFilter;
}

function formatRelativeTime(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffHours < 1) {
    const mins = Math.floor(diffMs / 60000);
    return `לפני ${mins} דקות`;
  }
  if (diffDays < 1) {
    return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays < 7) {
    return d.toLocaleDateString("he-IL", { weekday: "short" });
  }
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "numeric" });
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedPhone,
  isLoading,
  onSelect,
  onSearch,
  onStatusFilter,
  activeStatusFilter = 'all',
}) => {
  const [searchValue, setSearchValue] = useState("");
  const groups = groupByPhone(conversations);

  const handleSearch = (v: string) => {
    setSearchValue(v);
    onSearch(v);
  };

  const filterTabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'הכל' },
    { key: 'open', label: 'פתוח' },
    { key: 'pending', label: 'ממתין' },
    { key: 'solved', label: 'סגור' },
    { key: 'campaign', label: 'קמפיין' },
  ];

  return (
    <div
      style={{
        width: 300,
        minWidth: 300,
        borderRight: "1px solid #ddd",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        height: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "#3A7380",
          color: "#fff",
          padding: "16px 16px 12px",
          fontSize: 16,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 20 }}>💬</span>
        WhatsApp
      </div>

      {/* Status filter tabs */}
      {onStatusFilter && (
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          {filterTabs.map((tab) => (
            <button
              data-ai-id="whatsapp-status-filter"
              key={tab.key}
              onClick={() => onStatusFilter(tab.key)}
              style={{
                flex: 1,
                padding: "8px 4px",
                fontSize: 12,
                fontWeight: activeStatusFilter === tab.key ? 700 : 400,
                color: activeStatusFilter === tab.key ? "#3A7380" : "#666",
                background: "none",
                border: "none",
                borderBottom: activeStatusFilter === tab.key ? "2px solid #3A7380" : "2px solid transparent",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #eee" }}>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="חיפוש לפי שם, HM ID או טלפון..."
          style={{
            width: "100%",
            border: "1px solid #ddd",
            borderRadius: 20,
            padding: "8px 14px",
            fontSize: 13,
            outline: "none",
            backgroundColor: "#f5f5f5",
            direction: "rtl",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 13 }}>
            טוען שיחות...
          </div>
        )}

        {!isLoading && groups.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 13 }}>
            לא נמצאו שיחות
          </div>
        )}

        {groups.map((group) => {
          const isSelected = normalizePhone(group.phone_number) === (selectedPhone || "");
          const displayName = group.patient_name || "";
          const displayHmId = group.patient_hm_id || "";
          const preview = group.last_message_preview || "";
          const statusColor = STATUS_COLORS[group.status || "open"];

          return (
            <button
              key={group.phone_number}
              onClick={() => onSelect(group)}
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background: isSelected ? "#e8f4f5" : "transparent",
                border: "none",
                borderBottom: "1px solid #f0f0f0",
                cursor: "pointer",
                textAlign: "right",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = "transparent";
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  backgroundColor: "#3A7380",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                {(displayName || group.phone_number).charAt(0)}
                {/* Status dot */}
                <span
                  style={{
                    position: "absolute",
                    bottom: 1,
                    right: 1,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: statusColor,
                    border: "2px solid #fff",
                  }}
                />
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0, direction: "rtl" }}>
                {/* Top row: name + time */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#111",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {displayName || group.phone_number}
                  </span>
                  <span style={{ fontSize: 11, color: "#999", flexShrink: 0, marginRight: 4 }}>
                    {formatRelativeTime(group.last_message_at)}
                  </span>
                </div>

                {/* HM ID + phone row */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  {displayHmId && (
                    <span style={{ fontSize: 11, color: "#3A7380", fontWeight: 500 }}>
                      {displayHmId}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 11,
                      color: "#999",
                      direction: "ltr",
                      unicodeBidi: "plaintext",
                    }}
                  >
                    {group.phone_number}
                  </span>
                </div>

                {/* Assigned user */}
                {group.assigned_user_name && (
                  <div style={{ fontSize: 11, color: "#3A7380", marginBottom: 2 }}>
                    {group.assigned_user_name} · {STATUS_LABELS[group.status || "open"]}
                  </div>
                )}

                {/* Last message + unread badge */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {preview.slice(0, 45)}{preview.length > 45 ? "..." : ""}
                  </span>
                  {group.unread_count > 0 && (
                    <span
                      style={{
                        backgroundColor: "#25D366",
                        color: "#fff",
                        borderRadius: "50%",
                        minWidth: 18,
                        height: 18,
                        fontSize: 11,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 4px",
                        flexShrink: 0,
                        marginRight: 4,
                      }}
                    >
                      {group.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export type { PhoneGroup };
